import React, { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import styled, { keyframes } from "styled-components";

// Example category data for category conditionals
const categoryData = [
  {
    key: "role1",
    text: "Role 1",
    children: [
      { key: "child1", text: "Child 1" },
      { key: "child2", text: "Child 2" },
    ],
  },
  {
    key: "role2",
    text: "Role 2",
    children: [
      { key: "child3", text: "Child 3" },
      { key: "child4", text: "Child 4" },
    ],
  },
];

/* -------------------- Category Multi-Select (unchanged) ------------------ */
const CategoryMultiSelectDropdown = ({ options, selected, onChange }) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const containerRef = useRef(null);
  const menuRef = useRef(null);
  const [menuPosition, setMenuPosition] = useState(null);
  const MENU_MAX_HEIGHT = 250;

  const updateMenuPosition = () => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const availableSpaceBelow = window.innerHeight - rect.bottom;
      let top;
      if (availableSpaceBelow < MENU_MAX_HEIGHT) {
        top = rect.top + window.scrollY - MENU_MAX_HEIGHT;
      } else {
        top = rect.bottom + window.scrollY;
      }
      setMenuPosition({
        top,
        left: rect.left + window.scrollX,
        width: rect.width,
      });
    }
  };

  useEffect(() => {
    if (dropdownOpen) {
      updateMenuPosition();
    }
  }, [dropdownOpen]);

  useEffect(() => {
    if (dropdownOpen) {
      const handleScroll = () => updateMenuPosition();
      window.addEventListener("scroll", handleScroll);
      window.addEventListener("resize", updateMenuPosition);
      return () => {
        window.removeEventListener("scroll", handleScroll);
        window.removeEventListener("resize", updateMenuPosition);
      };
    }
  }, [dropdownOpen]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target) &&
        (!menuRef.current || !menuRef.current.contains(e.target))
      ) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filteredOptions = options
    .map((group) => {
      const filteredChildren = group.children.filter((child) =>
        child.text.toLowerCase().includes(searchQuery.toLowerCase())
      );
      return { ...group, children: filteredChildren };
    })
    .filter((group) => group.children.length > 0);

  const renderSelected = () => {
    if (!selected || selected.length === 0) {
      return <Placeholder>Select categories...</Placeholder>;
    }
    const selectedTexts = [];
    options.forEach((group) => {
      group.children.forEach((child) => {
        if (selected.includes(child.key)) {
          selectedTexts.push(child.text);
        }
      });
    });
    return selectedTexts.join(", ");
  };

  const toggleOption = (childKey, checked) => {
    let newSelected;
    if (checked) {
      newSelected = [...selected, childKey];
    } else {
      newSelected = selected.filter((x) => x !== childKey);
    }
    onChange(newSelected);
  };

  const menu =
    menuPosition && (
      <DropdownMenu
        ref={menuRef}
        style={{
          top: menuPosition.top,
          left: menuPosition.left,
          width: menuPosition.width,
          maxHeight: MENU_MAX_HEIGHT,
        }}
      >
        <SearchInput
          type="text"
          placeholder="Search categories..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        <OptionsList>
          {filteredOptions.map((group) => (
            <GroupContainer key={group.key}>
              <GroupTitle>{group.text}</GroupTitle>
              {group.children.map((child) => (
                <CheckboxContainer key={child.key}>
                  <Checkbox
                    type="checkbox"
                    checked={selected.includes(child.key)}
                    onChange={(e) => toggleOption(child.key, e.target.checked)}
                  />
                  <CheckboxLabel>{child.text}</CheckboxLabel>
                </CheckboxContainer>
              ))}
            </GroupContainer>
          ))}
        </OptionsList>
      </DropdownMenu>
    );

  return (
    <DropdownContainer ref={containerRef}>
      <DropdownHeader onClick={() => setDropdownOpen((prev) => !prev)}>
        {renderSelected()}
        <Arrow>{dropdownOpen ? "▲" : "▼"}</Arrow>
      </DropdownHeader>
      {dropdownOpen && ReactDOM.createPortal(menu, document.body)}
    </DropdownContainer>
  );
};

/* -------------------- ConditionalForm -------------------- */
/**
 * Props:
 * - initialData: { conditionals: [...] }
 * - onChange: callback({ conditionals })
 * - skipOptions: array of "previous question" data for skip, each item:
 *       { key, value, text, type, conditionalConfig, properties? }
 * - currentQuestionConfig: the entire config of the current question,
 *       including { conditionalConfig: { hasScore, allowedConditionals, 
 *         operatorRequired, thresholdType, followupMode }, 
 *         properties: { ... } }
 * - currentQuestionValue: string; to exclude from skip's "previous question" select
 */
const ConditionalForm = ({
  initialData = {},
  onChange,
  skipOptions = [],
  currentQuestionConfig = {}, // see componentTypes[x]
  currentQuestionValue = "",
  localProperties = {},
}) => {
  const [conditionals, setConditionals] = useState(initialData.conditionals || []);
  const [newConditionalType, setNewConditionalType] = useState("skip");

  const { conditionalConfig = {}, properties = {} } = currentQuestionConfig;
  const {
    allowedConditionals = { skip: false, followup: false },
    thresholdType,
    followupMode,
    // hasScore, operatorRequired, etc. if needed
  } = conditionalConfig;

  // Build the "allowed types" for new conditionals:
  const getAllowedNewTypes = () => {
    const types = [];
    if (allowedConditionals.skip) types.push("skip");
    if (allowedConditionals.followup) types.push("followup");
    // We'll assume "category" is always allowed
    types.push("category");
    return types;
  };


  useEffect(() => {
    setConditionals(initialData.conditionals || []);
  }, [initialData]);

  const saveConditionalsToParent = (newArray) => {
    setConditionals(newArray);
    onChange({ conditionals: newArray });
  };

  const addConditional = () => {
    // Collapse all existing expansions
    const collapsed = conditionals.map((cond) => ({ ...cond, expanded: false }));
    let newCond;
    switch (newConditionalType) {
      case "skip":
        newCond = {
          id: Date.now(),
          type: "skip",
          expanded: true,
          previousQuestion: "",
          operator: "gt",
          threshold: 0,
        };
        break;
      case "followup":
        newCond = {
          id: Date.now(),
          type: "followup",
          expanded: true,
          // defaults for numeric approach
          operator: "gt",
          threshold: 0,
          aiDriven: false,
          followupText: "",
          aiPrompt: "",
        };
        break;
      case "category":
      default:
        newCond = {
          id: Date.now(),
          type: "category",
          expanded: true,
          categoryOperator: "is",
          categoryChildren: [],
        };
        break;
    }
    saveConditionalsToParent([...collapsed, newCond]);
  };

  const removeConditional = (index) => {
    const updated = [...conditionals];
    updated.splice(index, 1);
    saveConditionalsToParent(updated);
  };

  const toggleExpand = (index) => {
    const updated = conditionals.map((cond, i) => ({
      ...cond,
      expanded: i === index ? !cond.expanded : false,
    }));
    saveConditionalsToParent(updated);
  };

  const updateConditional = (index, key, value) => {
    const updated = [...conditionals];
    updated[index] = { ...updated[index], [key]: value };
    saveConditionalsToParent(updated);
  };

  const updateConditionalMultiple = (index, changes) => {
    const updated = [...conditionals];
    updated[index] = { ...updated[index], ...changes };
    saveConditionalsToParent(updated);
  };

  const operatorLabel = (op) => {
    switch (op) {
      case "gt":
        return ">";
      case "lt":
        return "<";
      case "eq":
        return "=";
      case "includes":
        return "includes";
      case "excludes":
        return "excludes";
      default:
        return op;
    }
  };

  // Identify a question's threshold style (numeric vs. array) for skip:
  const getSkipQuestionThresholdType = (qValue) => {
    const found = skipOptions.find((q) => q.value === qValue);
    if (!found) return null;
    return found.conditionalConfig?.thresholdType || null;
  };

  const renderConditionalSummary = (cond) => {
    if (cond.type === "skip") {
      const qObj = skipOptions.find((q) => q.value === cond.previousQuestion);
      const qLabel = qObj ? qObj.text : "[No question selected]";

      if (!cond.previousQuestion) {
        return `Skip: (no question chosen)`;
      }
      const qThresholdType = getSkipQuestionThresholdType(cond.previousQuestion);

      if (qThresholdType === "numeric") {
        // e.g. Likert
        return `Skip: ${qLabel} ${operatorLabel(cond.operator)} ${
          Array.isArray(cond.threshold) ? cond.threshold.join(", ") : cond.threshold
        }`;
      } else if (qThresholdType === "array") {
        const chosen = Array.isArray(cond.threshold)
          ? cond.threshold.join(", ")
          : cond.threshold;
        return `Skip: ${qLabel} if user selected [${chosen}]`;
      }
      // fallback
      return `Skip: ${qLabel}`;
    }

    if (cond.type === "followup") {
      // For followup, we apply the currentQuestion's config
      if (thresholdType === "numeric") {
        return `Follow-up: ${operatorLabel(cond.operator)} ${
          Array.isArray(cond.threshold) ? cond.threshold.join(", ") : cond.threshold
        } - ${cond.aiDriven ? "AI" : "Manual"}`;
      } else if (followupMode === "options") {
        // includes/excludes
        const chosen = Array.isArray(cond.threshold)
          ? cond.threshold.join(", ")
          : cond.threshold;
        return `Follow-up: [${operatorLabel(cond.operator)} ${chosen}] - ${
          cond.aiDriven ? "AI" : "Manual"
        }`;
      }
      return `Follow-up (unrecognized mode)`;
    }

    if (cond.type === "category") {
      const cats = (cond.categoryChildren && cond.categoryChildren.join(", ")) || "none";
      return `Category: ${cond.categoryOperator} [${cats}]`;
    }

    return "";
  };

  return (
    <FormContainer>
      <h3>Conditionals</h3>

      {conditionals.map((cond, index) => {
        return (
          <ConditionalItemContainer key={cond.id}>
            <Header onClick={() => toggleExpand(index)}>
              <HeaderTitle>
                {renderConditionalSummary(cond) || `New ${cond.type} conditional`}
              </HeaderTitle>
              <ToggleButton>{cond.expanded ? "-" : "+"}</ToggleButton>
            </Header>

            {cond.expanded && (
              <ExpandedSection>
                {/* Type Selector: only show what's allowed by the current question config */}
                <FormField>
                  <Label>Type</Label>
                  <Select
                    value={cond.type}
                    onChange={(e) => updateConditional(index, "type", e.target.value)}
                  >
                    {/* Only show if skip is allowed */}
                    {allowedConditionals.skip && <option value="skip">Skip</option>}
                    {/* Only show if followup is allowed */}
                    {allowedConditionals.followup && (
                      <option value="followup">Follow-up</option>
                    )}
                    {/* Category always */}
                    <option value="category">Category</option>
                  </Select>
                </FormField>

                {/* --------------- SKIP --------------- */}
                {cond.type === "skip" && (
                  <>
                    <FormField>
                      <Label>Previous Question</Label>
                      <Select
                        value={cond.previousQuestion || ""}
                        onChange={(e) =>
                          updateConditional(index, "previousQuestion", e.target.value)
                        }
                      >
                        <option value="">-- Select Question --</option>
                        {skipOptions
                          // Only include questions that allow skip, and not the current question
                          .filter(
                            (q) =>
                              q.value !== currentQuestionValue &&
                              q.conditionalConfig?.allowedConditionals?.skip
                          )
                          .map((q) => (
                            <option key={q.key} value={q.value}>
                              {q.text}
                            </option>
                          ))}
                      </Select>
                    </FormField>

                    {cond.previousQuestion && (
                      (() => {
                        const qThresholdType = getSkipQuestionThresholdType(
                          cond.previousQuestion
                        );
                        if (qThresholdType === "numeric") {
                          // Show operator + numeric threshold(s)
                          return (
                            <>
                              <FormField>
                                <Label>Operator</Label>
                                <Select
                                  value={cond.operator}
                                  onChange={(e) => {
                                    const newOp = e.target.value;
                                    let newThreshold = cond.threshold;
                                    if (newOp === "eq" && !Array.isArray(newThreshold)) {
                                      newThreshold = [newThreshold];
                                    }
                                    if (
                                      (newOp === "gt" || newOp === "lt") &&
                                      Array.isArray(newThreshold)
                                    ) {
                                      newThreshold = newThreshold.length
                                        ? newThreshold[0]
                                        : 0;
                                    }
                                    updateConditionalMultiple(index, {
                                      operator: newOp,
                                      threshold: newThreshold,
                                    });
                                  }}
                                >
                                  <option value="gt">Greater than</option>
                                  <option value="lt">Less than</option>
                                  <option value="eq">Equal to</option>
                                </Select>
                              </FormField>

                              <FormField>
                                <Label>
                                  Threshold {cond.operator === "eq" && "(multiple ok)"}
                                </Label>
                                {cond.operator === "eq" ? (
                                  <Select
                                    multiple
                                    value={
                                      Array.isArray(cond.threshold)
                                        ? cond.threshold.map(String)
                                        : [String(cond.threshold)]
                                    }
                                    onChange={(e) =>
                                      updateConditional(
                                        index,
                                        "threshold",
                                        Array.from(e.target.selectedOptions).map((o) =>
                                          Number(o.value)
                                        )
                                      )
                                    }
                                  >
                                    {/* Example 0..10 range. 
                                        Or, you could read from the "scale" property if needed */}
                                    {Array.from({ length: 11 }, (_, i) => i).map((v) => (
                                      <option key={v} value={v}>
                                        {v}
                                      </option>
                                    ))}
                                  </Select>
                                ) : (
                                  <Select
                                    value={String(cond.threshold)}
                                    onChange={(e) =>
                                      updateConditional(
                                        index,
                                        "threshold",
                                        Number(e.target.value)
                                      )
                                    }
                                  >
                                    {Array.from({ length: 11 }, (_, i) => i).map((v) => (
                                      <option key={v} value={v}>
                                        {v}
                                      </option>
                                    ))}
                                  </Select>
                                )}
                              </FormField>
                            </>
                          );
                        } else if (qThresholdType === "array") {
                          // Show multi-select of that question’s "options"
                          const skipQ = skipOptions.find(
                            (q) => q.value === cond.previousQuestion
                          );
                          const theirOptions =
                            skipQ?.properties?.options || /* fallback */ [];
                          return (
                            <FormField>
                              <Label>Skip if user selected any of:</Label>
                              <Select
                                multiple
                                value={
                                  Array.isArray(cond.threshold)
                                    ? cond.threshold
                                    : [cond.threshold]
                                }
                                onChange={(e) =>
                                  updateConditional(
                                    index,
                                    "threshold",
                                    Array.from(e.target.selectedOptions).map((o) => o.value)
                                  )
                                }
                              >
                                {theirOptions.map((optVal) => (
                                  <option key={optVal} value={optVal}>
                                    {optVal}
                                  </option>
                                ))}
                              </Select>
                            </FormField>
                          );
                        }
                        return null;
                      })()
                    )}
                  </>
                )}

                {/* --------------- FOLLOW-UP --------------- */}
                {cond.type === "followup" && (
                  <>
                    {/*
                      For follow-up, we refer to the *current question’s* conditionalConfig:
                      - If thresholdType = "numeric", show numeric operator & threshold.
                      - If followupMode = "options", show includes/excludes + multi-select.
                    */}
                    {thresholdType === "numeric" && (
                      <>
                        <FormField>
                          <Label>Operator</Label>
                          <Select
                            value={cond.operator}
                            onChange={(e) => {
                              const newOp = e.target.value;
                              let newThreshold = cond.threshold;
                              if (newOp === "eq" && !Array.isArray(newThreshold)) {
                                newThreshold = [newThreshold];
                              }
                              if ((newOp === "gt" || newOp === "lt") && Array.isArray(newThreshold)) {
                                newThreshold = newThreshold.length ? newThreshold[0] : 0;
                              }
                              updateConditionalMultiple(index, {
                                operator: newOp,
                                threshold: newThreshold,
                              });
                            }}
                          >
                            <option value="gt">Greater than</option>
                            <option value="lt">Less than</option>
                            <option value="eq">Equal to</option>
                          </Select>
                        </FormField>

                        <FormField>
                          <Label>
                            Threshold {cond.operator === "eq" && "(Select one or more)"}
                          </Label>
                          {cond.operator === "eq" ? (
                            <Select
                              multiple
                              value={
                                Array.isArray(cond.threshold)
                                  ? cond.threshold.map(String)
                                  : [String(cond.threshold)]
                              }
                              onChange={(e) =>
                                updateConditional(
                                  index,
                                  "threshold",
                                  Array.from(e.target.selectedOptions).map((o) =>
                                    Number(o.value)
                                  )
                                )
                              }
                            >
                              {/* Example 0..10 range; could reference scale property */}
                              {Array.from({ length: 11 }, (_, i) => i).map((v) => (
                                <option key={v} value={v}>
                                  {v}
                                </option>
                              ))}
                            </Select>
                          ) : (
                            <Select
                              value={String(cond.threshold)}
                              onChange={(e) =>
                                updateConditional(index, "threshold", Number(e.target.value))
                              }
                            >
                              {Array.from({ length: 11 }, (_, i) => i).map((v) => (
                                <option key={v} value={v}>
                                  {v}
                                </option>
                              ))}
                            </Select>
                          )}
                        </FormField>
                      </>
                    )}

                    {followupMode === "options" && (
                      <>
                        <FormField>
                          <Label>Condition</Label>
                          <Select
                            value={cond.operator}
                            onChange={(e) => updateConditional(index, "operator", e.target.value)}
                          >
                            <option value="includes">Includes</option>
                            <option value="excludes">Excludes</option>
                          </Select>
                        </FormField>

                        <FormField>
                          <Label>
                            {cond.operator === "includes"
                              ? "Follow up if user selection includes:"
                              : "Follow up if user selection excludes:"}
                          </Label>
                          <Select
                            multiple
                            value={
                              Array.isArray(cond.threshold)
                                ? cond.threshold
                                : [cond.threshold]
                            }
                            onChange={(e) =>
                              updateConditional(
                                index,
                                "threshold",
                                Array.from(e.target.selectedOptions).map((o) => o.value)
                              )
                            }
                          >
                            {/* The current question’s "options" come from currentQuestionConfig.properties.options */}
                            {(localProperties.options || []).map((optVal) => (
                              <option key={optVal} value={optVal}>
                                {optVal}
                              </option>
                            ))}
                          </Select>
                        </FormField>
                      </>
                    )}

                    {/* Common followup fields: AI vs. manual */}
                    <FormField>
                      <Label>
                        <input
                          type="checkbox"
                          checked={cond.aiDriven}
                          onChange={(e) => updateConditional(index, "aiDriven", e.target.checked)}
                        />{" "}
                        AI Driven
                      </Label>
                    </FormField>
                    {cond.aiDriven ? (
                      <FormField>
                        <Label>AI Prompt</Label>
                        <Input
                          type="text"
                          placeholder="Enter AI prompt"
                          value={cond.aiPrompt}
                          onChange={(e) => updateConditional(index, "aiPrompt", e.target.value)}
                        />
                      </FormField>
                    ) : (
                      <FormField>
                        <Label>Follow-up Question</Label>
                        <Input
                          type="text"
                          placeholder="Enter follow-up question"
                          value={cond.followupText}
                          onChange={(e) => updateConditional(index, "followupText", e.target.value)}
                        />
                      </FormField>
                    )}
                  </>
                )}

                {/* --------------- CATEGORY --------------- */}
                {cond.type === "category" && (
                  <>
                    <FormField>
                      <Label>Category Operator</Label>
                      <Select
                        value={cond.categoryOperator}
                        onChange={(e) =>
                          updateConditional(index, "categoryOperator", e.target.value)
                        }
                      >
                        <option value="is">Is</option>
                        <option value="isNot">Is Not</option>
                      </Select>
                    </FormField>
                    <FormField>
                      <Label>Category Conditions</Label>
                      <CategoryMultiSelectDropdown
                        options={categoryData}
                        selected={cond.categoryChildren || []}
                        onChange={(newSelected) =>
                          updateConditional(index, "categoryChildren", newSelected)
                        }
                      />
                    </FormField>
                  </>
                )}

                {/* Delete */}
                <DeleteButton onClick={() => removeConditional(index)}>Delete</DeleteButton>
              </ExpandedSection>
            )}
          </ConditionalItemContainer>
        );
      })}

      {/* Add new conditional */}
      <AddConditionalContainer>
        <Select
          value={newConditionalType}
          onChange={(e) => setNewConditionalType(e.target.value)}
        >
          {getAllowedNewTypes().map((t) => {
            if (t === "skip") return <option key="skip" value="skip">Skip</option>;
            if (t === "followup") return <option key="followup" value="followup">Follow-up</option>;
            if (t === "category") return <option key="cat" value="category">Category</option>;
            return null;
          })}
        </Select>
        <AddButton onClick={addConditional}>Add Conditional</AddButton>
      </AddConditionalContainer>
    </FormContainer>
  );
};

export default ConditionalForm;

/* 
  ----------------------------------------------------
  Assume all of the styled-components (FormContainer, 
  ConditionalItemContainer, Header, ToggleButton, 
  FormField, Label, Select, Input, etc.) and 
  `keyframes` remain the same as in your original code. 
  ----------------------------------------------------
*/


/*
  ===========================================================================
    STYLES (FormContainer, ConditionalItemContainer, etc.) & keyframes 
    are assumed to remain the same as in your original code.
  ===========================================================================


/*
  ===========================================================================
    STYLES (FormContainer, ConditionalItemContainer, etc.) & keyframes 
    are assumed to remain the same as in your original code.
  ===========================================================================


/*
  NOTE: All styled-components (FormContainer, ConditionalItemContainer, 
  Header, HeaderTitle, ToggleButton, FormField, Label, Select, Input, 
  DeleteButton, AddConditionalContainer, AddButton, etc.) and the keyframes 
  import are assumed to be the same as your original code. 
  We've omitted them here for brevity, per your request.
*/




/* ================= Styled Components ================= */
const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  font-family: "Raleway", sans-serif;
  color: #2a3039;
`;

const ConditionalItemContainer = styled.div`
  border: 1px solid #ccc;
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 12px;
`;

const Header = styled.div`
  background: #f7f7f7;
  padding: 8px 12px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  transition: background 0.3s ease;
  &:hover {
    background: #ececec;
  }
`;

const HeaderTitle = styled.div`
  font-weight: bold;
`;

const ToggleButton = styled.div`
  font-size: 20px;
  line-height: 1;
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-5px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const ExpandedSection = styled.div`
  padding: 12px;
  background: #fff;
  display: flex;
  flex-direction: column;
  gap: 8px;
  animation: ${fadeIn} 0.3s ease-out;
`;

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 8px;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 4px;
`;

const Input = styled.input`
  padding: 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 14px;
  &:focus {
    outline: none;
    border-color: #2d70e2;
  }
`;

const Select = styled.select`
  padding: 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
  background: #fff;
  font-size: 14px;
  &:focus {
    outline: none;
    border-color: #2d70e2;
  }
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 8px;
`;

const PrimaryButton = styled.button`
  background-color: #2d70e2;
  color: #fff;
  border: none;
  border-radius: 4px;
  padding: 8px 16px;
  font-weight: 600;
  cursor: pointer;
  &:hover {
    background-color: #1c4db8;
  }
`;

const SecondaryButton = styled.button`
  background-color: #eee;
  color: #333;
  border: none;
  border-radius: 4px;
  padding: 8px 16px;
  font-weight: 600;
  cursor: pointer;
  &:hover {
    background-color: #ddd;
  }
`;

const AddConditionalContainer = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const AddButton = styled.button`
  background-color: #2d70e2;
  color: #fff;
  border: none;
  border-radius: 4px;
  padding: 6px 12px;
  cursor: pointer;
`;

const DeleteButton = styled.button`
  background-color: #f44336;
  color: #fff;
  border: none;
  border-radius: 4px;
  padding: 4px 8px;
  cursor: pointer;
  align-self: flex-end;
`;

/* ================ CategoryMultiSelectDropdown Styled Components ================ */
const DropdownContainer = styled.div`
  position: relative;
  border: 1px solid #ccc;
  border-radius: 4px;
  background: #fff;
  cursor: pointer;
  padding: 8px;
  font-family: "Raleway", sans-serif;
`;

const DropdownHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Arrow = styled.span`
  font-size: 12px;
`;

const Placeholder = styled.span`
  color: #999;
`;

const DropdownMenu = styled.div`
  position: absolute;
  z-index: 9999;
  background: #fff;
  border: 1px solid #ccc;
  border-top: none;
  padding: 8px;
  max-height: 250px;
  overflow-y: auto;
  animation: ${fadeIn} 0.3s ease-out;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 6px;
  margin-bottom: 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
`;

const OptionsList = styled.div`
  max-height: 150px;
  overflow-y: auto;
`;

const GroupContainer = styled.div`
  margin-bottom: 8px;
`;

const GroupTitle = styled.div`
  font-weight: bold;
  margin-bottom: 4px;
  color: #2d70e2;
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 4px;
`;

const Checkbox = styled.input`
  margin-right: 8px;
`;

const CheckboxLabel = styled.label`
  font-size: 14px;
`;
